/**
 * RTWO Cryptography & Identity Engine — Configuration Binding Service
 */

import {
  CRYPTO_CONFIGURATION_BINDINGS,
  CRYPTO_SERVICE_IDS,
  type RTWOCryptoConfigurationBinding,
} from '../domain-models';
import type { IdentityBackendType } from '../adapters/identity-backend-interface';
import type { KeyProviderType } from '../adapters/key-provider-interface';
import type {
  ConfigurationBindingResult,
  IConfigurationBindingService,
} from '../service-interfaces';
import type { IdentityBackendRegistry, KeyProviderRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';
import { isOperationalKeyProvider } from '../providers/crypto-provider-factory';

export class ConfigurationBindingService
  extends BaseCryptoService
  implements IConfigurationBindingService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.CONFIGURATION_BINDING;
  readonly serviceName = 'Configuration Binding Service';
  readonly description =
    'Binds runtime configuration to key provider and identity backend selection.';
  readonly layer = 'R7' as const;
  readonly integrations = ['Integration Framework', 'R7 Enterprise Services'];
  readonly requiresKeyProvider = false;
  readonly requiresIdentityBackend = false;

  private readonly bindings: RTWOCryptoConfigurationBinding[];

  constructor(
    context: CryptoServiceContext,
    private readonly keyProviderRegistry: KeyProviderRegistry,
    private readonly identityBackendRegistry: IdentityBackendRegistry
  ) {
    super(context);
    this.bindings = CRYPTO_CONFIGURATION_BINDINGS.map((binding, index) => ({
      bindingId: `cryp-binding-${index + 1}`,
      ...binding,
      boundAt: null,
    }));
  }

  async initialize(): Promise<void> {
    this.status = 'ready';
  }

  async bindKeyProvider(
    configurationId: string,
    providerType: KeyProviderType
  ): Promise<ConfigurationBindingResult> {
    const binding = this.bindings.find(
      (entry) => entry.configurationId === configurationId && entry.targetType === 'key_provider'
    );
    if (!binding) {
      return this.unavailableResult(null, 'Configuration binding not found');
    }

    if (providerType === 'not_configured') {
      return this.unavailableResult(
        binding.bindingId,
        'Provider type not_configured cannot be bound'
      );
    }

    const provider = this.keyProviderRegistry.getProviderByType(providerType);
    if (!provider) {
      return this.unavailableResult(
        binding.bindingId,
        `No key provider registered for type: ${providerType}`
      );
    }

    this.keyProviderRegistry.setActiveProvider(provider.providerId);
    const operational = isOperationalKeyProvider(providerType);
    binding.status = operational ? 'active' : 'bound';
    binding.providerType = providerType;
    binding.boundAt = Date.now();

    this.context.eventBus.emit(CryptoEventTypes.CONFIGURATION_BOUND, configurationId, {
      configurationId,
      targetType: 'key_provider',
      providerType,
      scaffoldOnly: !operational,
    });

    return {
      success: true,
      bindingId: binding.bindingId,
      targetType: 'key_provider',
      providerType,
      error: null,
      source: operational
        ? 'Cryptography Provider — Key Provider Bound'
        : 'Cryptography Provider — Key Provider Bound (Scaffold — Operations Unavailable)',
      scaffoldOnly: !operational,
    };
  }

  async bindIdentityBackend(
    configurationId: string,
    backendType: IdentityBackendType
  ): Promise<ConfigurationBindingResult> {
    const binding = this.bindings.find(
      (entry) =>
        entry.configurationId === configurationId && entry.targetType === 'identity_backend'
    );
    if (!binding) {
      return this.unavailableResult(null, 'Configuration binding not found');
    }

    if (backendType === 'not_configured') {
      return this.unavailableResult(
        binding.bindingId,
        'Backend type not_configured cannot be bound'
      );
    }

    const backend = this.identityBackendRegistry.getBackendByType(backendType);
    if (!backend) {
      return this.unavailableResult(
        binding.bindingId,
        `No identity backend registered for type: ${backendType}`
      );
    }

    this.identityBackendRegistry.setActiveBackend(backend.backendId);
    binding.status = 'bound';
    binding.providerType = backendType;
    binding.boundAt = Date.now();

    this.context.eventBus.emit(CryptoEventTypes.CONFIGURATION_BOUND, configurationId, {
      configurationId,
      targetType: 'identity_backend',
      backendType,
      scaffoldOnly: true,
    });

    return {
      success: true,
      bindingId: binding.bindingId,
      targetType: 'identity_backend',
      providerType: backendType,
      error: null,
      source: 'Cryptography Provider — Identity Backend Bound (Scaffold — Operations Unavailable)',
      scaffoldOnly: true,
    };
  }

  getBinding(bindingId: string): RTWOCryptoConfigurationBinding | null {
    const binding = this.bindings.find((entry) => entry.bindingId === bindingId);
    return binding ? { ...binding } : null;
  }

  getAllBindings(): RTWOCryptoConfigurationBinding[] {
    return this.bindings.map((binding) => ({ ...binding }));
  }

  getActiveBindingCount(): number {
    return this.bindings.filter(
      (binding) => binding.status === 'bound' || binding.status === 'active'
    ).length;
  }

  private unavailableResult(bindingId: string | null, error: string): ConfigurationBindingResult {
    return {
      success: false,
      bindingId,
      targetType: null,
      providerType: null,
      error,
      source: 'Cryptography Provider — Configuration Not Bound',
    };
  }
}
